<template>
  <Dialog
    v-model:visible="isConfirmationPresenceModalVisible"
    header="Confirmation de votre présence"
    modal
    @after-hide="resetModelValue"
  >
    <PresenceForm
      v-model="confirmationPresence"
      @submitted="onFormSubmitted"
    />
  </Dialog>

  <Dialog
    v-model:visible="showJokeDialog"
    header="Merci !"
    modal
    :closable="false"
  >
    <p class="joke-dialog__text">
      Votre réponse a bien été enregistrée.
    </p>
    <p class="joke-dialog__text">
      Une dernière formalité&hellip;
    </p>

    <div class="joke-dialog__checkbox">
      <Checkbox
        v-model="jokeAccepted"
        input-id="joke_accept"
        binary
      />
      <label
        for="joke_accept"
        class="joke-dialog__label"
      >
        Je certifie ne pas venir habillé(e) en blanc
      </label>
    </div>

    <div class="joke-dialog__footer">
      <button
        class="btn"
        :disabled="!jokeAccepted"
        @click="onJokeConfirm"
      >
        J'accepte les conditions
      </button>
    </div>
  </Dialog>
  <div class="accueil">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero__overlay" />
      <div class="hero__content">
        <h1 class="hero__title">
          Elena & Antoine
        </h1>
        <h1 class="hero__subtitle">
          Nous allons nous marier&nbsp;!
        </h1>
        <div class="hero__divider" />
        <p class="hero__details">
          Nous nous dirons oui le <strong>samedi 13 juin 2026</strong>,<br>
          au Château du Vivier, à Fontenay-Trésigny (77).
        </p>
        <button
          class="btn"
          @click="isConfirmationPresenceModalVisible = true"
        >
          Confirmez votre présence
        </button>
      </div>
    </section>

    <!-- Notre histoire -->
    <section class="histoire">
      <div class="histoire__inner">
        <h2 class="histoire__title">
          Elena & Antoine
        </h2>
        <p class="histoire__subtitle">
          Notre histoire
        </p>
        <p class="histoire__intro">
          Le coup de foudre, cela n'arrive pas que dans les films&nbsp;!
        </p>

        <div class="histoire__timeline">
          <div class="histoire__moment">
            <div class="histoire__photo-wrap">
              <img
                src="../images/received_311522469882492.jpeg"
                alt="Elena & Antoine"
                class="histoire__photo"
              >
            </div>
            <div class="histoire__text">
              <p class="histoire__date">
                4 décembre 2018
              </p>
              <p>Il murmure un &laquo;&nbsp;je t'apprécie&nbsp;&raquo; avec pudeur, le sourire aux lèvres comme pour me dire un premier &laquo;&nbsp;je t'aime&nbsp;&raquo;</p>
            </div>
          </div>

          <div class="histoire__moment histoire__moment--reverse">
            <div class="histoire__photo-wrap">
              <img
                src="../images/IMG_2332.jpeg"
                alt="Elena & Antoine"
                class="histoire__photo"
              >
            </div>
            <div class="histoire__text">
              <p class="histoire__date">
                18 août 2024
              </p>
              <p>Elle me répond &laquo;&nbsp;oui&nbsp;&raquo; au soleil levant, les yeux mouillés, en haut des falaises de l'Algarve</p>
            </div>
          </div>

          <div class="histoire__moment">
            <div class="histoire__photo-wrap">
              <img
                src="../images/Amour-Etc.196 (1).jpg"
                alt="Elena & Antoine"
                class="histoire__photo"
              >
            </div>
            <div class="histoire__text">
              <p class="histoire__date">
                13 juin 2026
              </p>
              <p>Nous nous dirons &laquo;&nbsp;je le veux&nbsp;&raquo; les yeux dans les yeux, et célébrerons notre union à vos côtés</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Confirmation CTA -->
    <section class="confirmation">
      <div class="confirmation__inner">
        <p>Pour nous aider à finaliser l'organisation, merci de répondre au petit questionnaire ci-dessous.</p>
        <button
          class="btn"
          @click="isConfirmationPresenceModalVisible = true"
        >
          Confirmez votre présence
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { useToast } from "primevue";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import { ref } from "vue";

import PresenceForm from "../components/PresenceForm.vue";

const toast = useToast()

const createConfirmationPresenceBaseModel = () => ({
    first_name: null,
    last_name: null,
    is_absent: false,
    secular_ceremony_presence: false,
    dinner_presence: false,
    sunday_brunch_presence: false,
    diet_indications: "",
})

const isConfirmationPresenceModalVisible = ref(false)
const confirmationPresence = ref(createConfirmationPresenceBaseModel())
const showJokeDialog = ref(false)
const jokeAccepted = ref(false)

const resetModelValue = () => {
  confirmationPresence.value = createConfirmationPresenceBaseModel()
}

const onFormSubmitted = ({ isAbsent }) => {
  isConfirmationPresenceModalVisible.value = false
  if (isAbsent) {
    toast.add({
      severity: "success",
      summary: "Merci !",
      detail: "Votre réponse a bien été enregistrée.",
      life: 5000,
    })
  } else {
    showJokeDialog.value = true
  }
}

const onJokeConfirm = () => {
  showJokeDialog.value = false
  jokeAccepted.value = false
  toast.add({
    severity: "success",
    summary: "Merci !",
    detail: "Votre réponse a bien été enregistrée.",
    life: 5000,
  })
}
</script>

<style lang="scss" scoped>
@import "../base/mixins";

/* ─── Hero ──────────────────────────────────────────────── */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-image: url('../images/IMG_9192.jpeg');
  background-size: cover;
  background-position: center;

  @media (max-width: $bp-mobile) {
    background-position: 44% 5%;
  }
}

.hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba($color-bg-primary, 0.3) 0%,
    rgba($color-bg-primary, 0.6) 50%,
    rgba($color-bg-primary, 0.85) 100%
  );
}

.hero__content {
  position: relative;
  z-index: 1;
  padding: $spacing-section-y-desktop $spacing-section-x $spacing-section-y;
  max-width: $container-narrow;
}

.hero__title {
  font-size: 2.6rem;
  font-weight: 300;
  letter-spacing: 0.02em;
  margin-bottom: $spacing-block-sm;

  @media (min-width: $bp-tablet) {
    font-size: 4rem;
  }
}

.hero__subtitle {
  font-size: 1.8rem;
  font-weight: 300;
  letter-spacing: 0.02em;
  margin-bottom: $spacing-block-sm;

  @media (min-width: $bp-tablet) {
    font-size: 3rem;
  }
}

.hero__divider {
  width: 60px;
  height: 1px;
  background-color: $color-accent;
  margin: 0 auto $spacing-block-sm;
}

.hero__details {
  @include body-text;
  font-size: 1.05rem;
  line-height: 2;
  margin-bottom: $spacing-block;

  strong {
    font-weight: 400;
  }
}

/* ─── Button ───────────────────────────────────────────── */
.btn {
  @include body-text;
  display: inline-block;
  padding: 14px 40px;
  border: 1px solid $color-text-primary;
  background: transparent;
  color: $color-text-primary;
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: $color-text-primary;
    color: $color-bg-primary;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: transparent;
      color: $color-text-primary;
    }
  }
}

/* ─── Histoire ─────────────────────────────────────────── */
.histoire {
  @include section-padding;
  background-color: $color-bg-primary;
}

.histoire__inner {
  @include container;
  text-align: center;
}

.histoire__title {
  font-size: 2.2rem;
  font-weight: 300;
  margin-bottom: $spacing-element-sm;

  @media (min-width: $bp-tablet) {
    font-size: 3rem;
  }
}

.histoire__subtitle {
  @include body-text;
  font-size: 0.85rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: $color-text-secondary;
  margin-bottom: $spacing-element;
}

.histoire__intro {
  @include body-text;
  font-size: 1.1rem;
  font-style: italic;
  font-family: $font-heading;
  color: $color-text-secondary;
  margin-bottom: $spacing-block-lg;
}

.histoire__timeline {
  display: flex;
  flex-direction: column;
  gap: $spacing-block-lg;

  @media (min-width: $bp-tablet) {
    gap: $spacing-section-y;
  }
}

.histoire__moment {
  display: flex;
  flex-direction: column;
  gap: $spacing-block;
  text-align: left;

  @media (min-width: $bp-tablet) {
    flex-direction: row;
    align-items: center;
    gap: $spacing-block;
  }

  &--reverse {
    @media (min-width: $bp-tablet) {
      flex-direction: row-reverse;
    }
  }
}

.histoire__photo-wrap {
  flex-shrink: 0;
  overflow: hidden;
  max-width: 450px;
  max-height: 562px;

  @media (min-width: $bp-tablet) {
    width: 45%;
  }
}

.histoire__photo {
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
}

.histoire__text {
  @media (min-width: $bp-tablet) {
    flex: 1;
  }

  h3 {
    margin-bottom: $spacing-element;
  }

  p {
    @include body-text;
  }
}

.histoire__date {
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: $color-accent;
  margin-bottom: $spacing-element-sm;
}

/* ─── Confirmation ─────────────────────────────────────── */
.confirmation {
  @include section-padding;
  background-color: $color-bg-secondary;
  text-align: center;
}

.confirmation__inner {
  @include container;

  h2 {
    margin-bottom: $spacing-element;
  }

  p {
    @include body-text;
    max-width: 500px;
    margin: 0 auto $spacing-block;
  }
}

/* ─── Joke Dialog ─────────────────────────────────────── */
.joke-dialog__text {
  @include body-text;
  text-align: center;
  line-height: 1.6;

  & + & {
    margin-top: $spacing-element-sm;
  }
}

.joke-dialog__checkbox {
  display: flex;
  align-items: center;
  gap: $spacing-element;
  margin-top: $spacing-block;
  padding: $spacing-element;
  border: 1px solid $color-accent;
}

.joke-dialog__label {
  @include body-text;
  font-size: 0.85rem;
  font-style: italic;
  cursor: pointer;
}

.joke-dialog__footer {
  display: flex;
  justify-content: center;
  margin-top: $spacing-block;
}
</style>
